import { Events, PageContent } from '../../content/discover/content';
import EventItem from './EventItem';

type EventSectionProps = {
  events: Events;
  /** When both are empty/falsy, the description block is not rendered. */
  descriptionTitle?: PageContent['eventSectionDescription']['title'];
  descriptionText?: PageContent['eventSectionDescription']['description'];
  /** Optional. Override section background, e.g. "bg-gray-50" for alternating layout. Default: "bg-white". */
  sectionClassName?: string;
  /** Optional. Override the inner container sizing/padding. */
  containerClassName?: string;
  /** Translation toggle handler */
  onTranslationToggle?: () => void;
  /** Translation loading state */
  isTranslating?: boolean;
  /** Show translate button */
  showTranslateButton?: boolean;
};

const EventSection = ({ 
  events, 
  descriptionTitle, 
  descriptionText, 
  sectionClassName, 
  containerClassName,
  onTranslationToggle,
  isTranslating = false,
  showTranslateButton = false
}: EventSectionProps) => {
  const hasEvents = !!(events?.events && events.events.length > 0);
  const hasDescription = !!(descriptionTitle || descriptionText);

  // Render if there are events, description, or translation button
  if (!hasEvents && !hasDescription && !showTranslateButton) return null;

  const getGridCols = () => {
    if (!events.events || events.events.length === 0) return 'grid-cols-1';
    if (events.events.length <= 2) return 'grid-cols-1 md:grid-cols-2';
    return 'grid-cols-1 md:grid-cols-3';
  };

  const isCompactPadding = !!(events.events && events.events.length >= 3);

  return (
    <section className={sectionClassName || 'bg-white'}>
      <div className={containerClassName ?? "container mx-auto px-4 md:px-8 pt-0 pb-16 w-full max-w-[1280px]"}>
        {showTranslateButton && (
          <div className="flex justify-end mb-6">
            <button
              onClick={onTranslationToggle}
              disabled={isTranslating}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              aria-label="Translate this section"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
              </svg>
              <span>{isTranslating ? 'Translating...' : 'Translate this section'}</span>
            </button>
          </div>
        )}
        {events.events && events.events.length > 0 && (
          <div>
            <div className={`grid ${getGridCols()} gap-4`}>
              {events.events.map((event) => (
                <EventItem key={event.id} event={event} isFourCols={isCompactPadding} />
              ))}
            </div>
          </div>
        )}
        {(descriptionTitle || descriptionText) && (
          <div className={`flex flex-col w-full max-w-[1008px] mx-auto gap-5 ${(!events.events || events.events.length === 0) ? 'mt-0' : 'mt-12'}`}>
            {descriptionTitle && (
              <h3 className="text-2xl md:text-3xl font-semibold text-gray-900 leading-9 md:tracking-[-0.72px]">
                {descriptionTitle}
              </h3>
            )}
            {descriptionText && (
              <p className="text-base leading-6 font-normal text-gray-600">
                {descriptionText}
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default EventSection; 